// Login.jsx - Complete Fix
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Snackbar, Alert, Button } from "@mui/material";
import "./Login.css";
import { useUser } from "./UserContextPersistent";
import HomeHeader from "../../components/header/homeHeader/HomeHeader";
import StandardLoginForm from "./StandardLoginForm";
import PhoneInput from "./PhoneInput";
import ForgotPasswordForm from "./ForgotPasswordForm";
import SocialLogin from "./SocialLogin";
import RegistrationPrompt from "./RegistrationPrompt";
import OtpVerification from "./OtpVerification";
import { otp } from "../../config/Pageitems";
import LoginLeftContainer from "./LoginLeftContainer";
import Footer from "../../components/common/Footer";

const Login = () => {
  const navigate = useNavigate();
  const { dispatch, updateDemo, state } = useUser();
  const navigationTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  // Clear previously selected app state on login page load
  useEffect(() => {
    localStorage.removeItem("selectedApp");
  }, []);

  // Message state
  const [notification, setNotification] = useState({
    message: "",
    type: "success",
    open: false,
  });
  const [loading, setLoading] = useState(false);

  // View state
  const [showOtpFlow, setShowOtpFlow] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Auth state
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  // Generic notification handler
  const showNotification = (message, type = "success") => {
    if (!mountedRef.current) return;

    setNotification({
      message,
      type,
      open: true,
    });
  };

  // Reset authentication state
  const resetAuthState = () => {
    sessionStorage.removeItem("authToken");
    const cookies = new (require("universal-cookie"))();
    cookies.remove("token", { path: "/" });
    setConfirmationResult(null);
    dispatch({ type: "CLEAR_USER" });
  };

  // Handle navigating back to login form
  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setShowOtpFlow(false);
    setShowOtpInput(false);
  };

  // Handle OTP workflow navigation
  const handleOTPLogin = () => {
    setShowOtpFlow(true);
    setShowForgotPassword(false);
    setShowOtpInput(false);
    resetAuthState();
  };

  // Handle forgot password navigation
  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setShowOtpFlow(false);
    setShowOtpInput(false);
  };

  const handleLoginSuccess = (
    message = "Login successful!",
    userData = null,
  ) => {
    if (
      message === "Phone verification successful!" ||
      message === "Login successful!" ||
      message === "Google login successful!"
    ) {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      console.log("=== LOGIN SUCCESS DEBUG ===");
      console.log("userData received:", userData);
      console.log("state.organization:", state.organization);

      let orgData = state.organization;

      // STEP 1: Try to get organization from localStorage userState
      if (!orgData) {
        try {
          const userState = JSON.parse(localStorage.getItem("userState"));
          orgData = userState?.organization;
          console.log("orgData from userState:", orgData);
        } catch (e) {
          console.error("Error parsing userState:", e);
        }
      }

      // STEP 2: Try to get organization from localStorage "organization" key
      if (!orgData) {
        try {
          const storedOrg = localStorage.getItem("organization");
          if (storedOrg) {
            orgData = JSON.parse(storedOrg);
            console.log("orgData from localStorage organization:", orgData);
          }
        } catch (e) {
          console.error("Error parsing organization:", e);
        }
      }

      // STEP 3: Last resort - check if userData has organization property
      if (!orgData && userData?.organization) {
        orgData = userData.organization;
        console.log("orgData from userData.organization:", orgData);
      }

      console.log("Final orgData:", orgData);
      console.log("Final orgData.organizationType:", orgData?.organizationType);

      if (orgData?.organizationType) {
        const orgTypes = Array.isArray(orgData.organizationType)
          ? orgData.organizationType
          : [orgData.organizationType];

        console.log("Organization types:", orgTypes);
        const appCount = orgTypes.length;
        console.log("App count:", appCount);

        if (appCount > 1) {
          // For multi-app orgs, navigate to app selection page
          navigationTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              navigate("/app-select");
            }
          }, 300);
          return;
        } else if (appCount === 1) {
          navigationTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              navigateToApp(orgData);
            }
          }, 300);
        } else {
          navigate("/dashboard");
        }
      } else {
        console.log("No organizationType found, going to dashboard");
        navigate("/dashboard");
      }
    }
  };

  const navigateToApp = (orgData) => {
    console.log("Navigating to app with orgData:", orgData);

    if (orgData?.organizationType && Array.isArray(orgData.organizationType)) {
      const orgTypes = orgData.organizationType;

      if (orgTypes.includes("vehicleTrack")) {
        localStorage.setItem("selectedApp", "vehicleTrack");
        navigate("/dashboard");
      } else if (orgTypes.includes("schoolTrack")) {
        localStorage.setItem("selectedApp", "schoolTrack");
        navigate("/school");
      } else if (orgTypes.includes("staffTrack")) {
        localStorage.setItem("selectedApp", "staffTrack");
        window.location.href = "https://staff.routeeye.io";
      } else if (orgTypes.includes("custom")) {
        localStorage.setItem("selectedApp", "custom");
        navigate("/dashboard");
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/dashboard");
    }
  };

  const handleLoginError = (errorMessage) => {
    showNotification(errorMessage, "error");
  };

  return (
    <>
      <HomeHeader />
      <div className="login_container">
        <div className="content_container">
          <LoginLeftContainer />

          <div
            className="container_container2"
            style={{
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            {showOtpFlow ? (
              showOtpInput ? (
                <OtpVerification
                  phoneNumber={phoneNumber}
                  confirmationResult={confirmationResult}
                  loading={loading}
                  setLoading={setLoading}
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                  handleBackToLogin={handleBackToLogin}
                  dispatch={dispatch}
                  navigate={navigate}
                  resetAuthState={resetAuthState}
                />
              ) : (
                <PhoneInput
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  loading={loading}
                  setLoading={setLoading}
                  setShowOtpInput={setShowOtpInput}
                  setConfirmationResult={setConfirmationResult}
                  setSuccessMessage={(msg) => showNotification(msg)}
                  setErrorMessage={(msg) => showNotification(msg, "error")}
                  setOpen={() => {}}
                  handleBackToLogin={handleBackToLogin}
                />
              )
            ) : showForgotPassword ? (
              <ForgotPasswordForm
                loading={loading}
                setLoading={setLoading}
                handleBackToLogin={handleBackToLogin}
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
              />
            ) : (
              <>
                <StandardLoginForm
                  loading={loading}
                  setLoading={setLoading}
                  handleOTPLogin={handleOTPLogin}
                  handleForgotPassword={handleForgotPassword}
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                  dispatch={dispatch}
                  navigate={navigate}
                  updateDemo={updateDemo}
                />
                <SocialLogin
                  loading={loading}
                  setLoading={setLoading}
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                  dispatch={dispatch}
                  navigate={navigate}
                />
                <RegistrationPrompt navigate={navigate} />
              </>
            )}
          </div>
        </div>
      </div>
      <Footer isLogin={true} />

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => {
          if (mountedRef.current) {
            setNotification((prev) => ({ ...prev, open: false }));
          }
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ marginTop: "70px" }}
      >
        <Alert
          onClose={() => {
            if (mountedRef.current) {
              setNotification((prev) => ({ ...prev, open: false }));
            }
          }}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
